import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TabletContext = createContext(null);

export const TabletProvider = ({ children }) => {
    // Detect tablet based on screen width (768px - 1366px)
    const [isTablet, setIsTablet] = useState(false);
    const [sidebarExpanded, setSidebarExpanded] = useState(false);
    const [tabletMode, setTabletMode] = useState('auto'); // 'auto', 'force-tablet', 'force-desktop'
    
    // Check if device is tablet (NOT phone)
    const checkIsTablet = useCallback(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        // PHONES are NEVER tablets - strict mobile breakpoint
        // Mobile phones are typically < 768px width in portrait
        if (width < 768) {
            return false;
        }
        
        if (tabletMode === 'force-tablet') {
            return true;
        }
        if (tabletMode === 'force-desktop') {
            return false;
        }
        
        // Tablet detection: 768px - 1366px width
        // Only consider as tablet if it's truly a tablet-sized device
        const isTabletWidth = width >= 768 && width <= 1366;
        
        // Additional check: tablets usually have aspect ratio closer to 4:3 or 3:4
        // Phones in landscape have very wide aspect ratios (> 2:1)
        const aspectRatio = Math.max(width, height) / Math.min(width, height);
        const isPhoneInLandscape = aspectRatio > 1.8 && Math.min(width, height) < 500;
        
        if (isPhoneInLandscape) {
            return false;
        }
        
        return isTabletWidth;
    }, [tabletMode]);
    
    useEffect(() => {
        const handleResize = () => {
            setIsTablet(checkIsTablet());
        };
        
        // Initial check
        handleResize();
        
        // Listen for resize events
        window.addEventListener('resize', handleResize);
        
        // Also check on orientation change for iPads
        window.addEventListener('orientationchange', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [checkIsTablet]);
    
    // Toggle sidebar expansion
    const toggleSidebar = useCallback(() => {
        setSidebarExpanded(prev => !prev);
    }, []);
    
    // Expand sidebar temporarily (for hover)
    const expandSidebar = useCallback(() => {
        setSidebarExpanded(true);
    }, []);
    
    // Collapse sidebar
    const collapseSidebar = useCallback(() => {
        setSidebarExpanded(false);
    }, []);
    
    // Force tablet mode
    const forceTabletMode = useCallback(() => {
        setTabletMode('force-tablet');
        localStorage.setItem('tablet_mode', 'force-tablet');
    }, []);
    
    // Force desktop mode
    const forceDesktopMode = useCallback(() => {
        setTabletMode('force-desktop');
        localStorage.setItem('tablet_mode', 'force-desktop');
    }, []);
    
    // Auto mode
    const setAutoMode = useCallback(() => {
        setTabletMode('auto');
        localStorage.setItem('tablet_mode', 'auto');
    }, []);
    
    // Load saved mode on mount
    useEffect(() => {
        const savedMode = localStorage.getItem('tablet_mode');
        if (savedMode && ['auto', 'force-tablet', 'force-desktop'].includes(savedMode)) {
            setTabletMode(savedMode);
        }
    }, []);
    
    // Recalculate when mode changes
    useEffect(() => {
        setIsTablet(checkIsTablet());
    }, [tabletMode, checkIsTablet]);
    
    const value = {
        isTablet,
        sidebarExpanded,
        tabletMode,
        toggleSidebar,
        expandSidebar,
        collapseSidebar,
        forceTabletMode,
        forceDesktopMode,
        setAutoMode
    };
    
    return (
        <TabletContext.Provider value={value}>
            {children}
        </TabletContext.Provider>
    );
};

export const useTablet = () => {
    const context = useContext(TabletContext);
    if (!context) {
        throw new Error('useTablet must be used within a TabletProvider');
    }
    return context;
};

export default TabletContext;
